// /lib/errors/handleAppError.ts

import type { AxiosError } from 'axios'

import { toast } from 'sonner'
import { ApiError, ValidationError } from './apiError'
import { isNoAvailabilityError } from './availability'
import { BookingAction } from '../../state/booking.reducer'
import { BookingT } from '@/src/types/i18n'

/**
 * App-level error types your UI understands.
 * These are BUSINESS errors — not HTTP details.
 */
export type AppError =
  | 'cart_expired'
  | 'cart_not_found'
  | 'insufficient_availability'
  | 'payment_failed'
  | 'bad_request'
  | 'unauthorized'
  | 'network_error'
  | 'internal_error'
  | 'validation_error'
  | 'unknown'

/**
 * Optional: allow injecting custom behaviour like redirects
 */
type HandleOptions = {
  onValidationError?: (errors: ValidationError[]) => void
  onCartExpired?: () => void
  onCartNotFound?: () => void
  onUnauthorized?: () => void
  onInternalError?: () => void
}

/**
 * Map raw error -> AppError
 */
function mapApiError(error: unknown): AppError {
  // Network error (no response)
  if (isAxiosNetworkError(error)) {
    return 'network_error'
  }

  // The API reports a full or off-sale session under several statuses (see availability.ts).
  if (isNoAvailabilityError(error)) return 'insufficient_availability'

  if (error instanceof ApiError) {
    // services/api.ts wraps axios errors in ApiError, so a timeout or offline request lands here.
    if (error.isNetworkError) return 'network_error'

    const status = error.status
    const code = error.data?.errorCode

    // HTTP-based mapping
    if (status === 400) return 'bad_request'
    if (status === 401) return 'unauthorized'
    if (status === 404) return 'cart_not_found'

    if (status === 409) {
      if (code === 'CART_EXPIRED') return 'cart_expired'
      if (code === 'INSUFFICIENT_AVAILABILITY') return 'insufficient_availability'
    }

    if (status === 422) return 'validation_error'

    if (status && status >= 500) return 'internal_error'
    if (code === 'PAYMENT_FAILED') return 'payment_failed'
  }

  return 'unknown'
}

function isAxiosNetworkError(error: unknown): boolean {
  const err = error as AxiosError
  return !!err?.isAxiosError && !err.response
}

/**
 * Centralised UI error handler
 */
export function handleAppError(
  error: unknown,
  //options?: HandleOptions,
  args: { dispatch: React.Dispatch<BookingAction>; t: BookingT },
): AppError {
  const { dispatch, t } = args
  const appError = mapApiError(error)
  const options: HandleOptions = {}

  const setError = (error: string) => {
    dispatch({
      type: 'SET_ERROR',
      error: {
        level: 'medium',
        scope: 'local',
        message: error,
      },
    })
  }

  switch (appError) {
    case 'validation_error':
      const err = error as ApiError
      if (err && err.data && err?.data?.errors) options?.onValidationError?.(err.data.errors)
      return appError

    case 'cart_expired':
      setError(t('ticket.errors.cartExpired'))
      toast.error(t('ticket.errors.cartExpired'))
      options?.onCartExpired?.()
      return appError

    case 'cart_not_found':
      setError(t('ticket.errors.cartExpired'))
      toast.error(t('ticket.errors.cartExpired'))
      options?.onCartNotFound?.()
      return appError

    case 'insufficient_availability':
      toast.error(t('ticket.errors.ticketsUnavailable'))
      return appError

    case 'payment_failed':
      toast.error(t('ticket.errors.paymentFailed'))
      return appError

    case 'unauthorized':
      toast.error(t('ticket.errors.generic'))
      options?.onUnauthorized?.()
      return appError

    case 'internal_error':
      toast.error(t('ticket.errors.generic'))
      options?.onInternalError?.()
      return appError

    case 'bad_request':
      setError(t('ticket.errors.badRequest'))
      toast.error(t('ticket.errors.badRequest'))
      return appError

    case 'network_error':
      toast.error(t('ticket.errors.network'))
      return appError

    case 'unknown':
    default:
      toast.error(t('ticket.errors.generic'))
      return appError
  }
}
